import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Custom element: <hero-slider>
 * Renders a crossfade hero slider with autoplay, keyboard nav, arrows, and thumbnails.
 */
class HeroSlider extends Component {
  #current = 0;
  #interval = null;
  #isHovered = false;
  #isVisible = true;
  #visibilityObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.slides = Array.from(this.querySelectorAll('.hero-slide'));
    this.thumbnails = Array.from(this.querySelectorAll('.hero-slider__thumbnail'));
    this.prevBtn = this.querySelector('.hero-slider-arrow--previous');
    this.nextBtn = this.querySelector('.hero-slider-arrow--next');

    if (this.slides.length <= 1) return;

    this.#bindEvents();
    this.#setupVisibilityObserver();

    if (this.autoplay) this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#stop();
    this.#visibilityObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  get autoplay() {
    return this.dataset.autoplay === 'true';
  }

  get autoplaySpeed() {
    const s = parseInt(this.dataset.autoplaySpeed, 10);
    return (isNaN(s) ? 6 : s) * 1000;
  }

  get infinite() {
    return this.dataset.infinite === 'true';
  }

  get pauseOnHover() {
    return this.dataset.pauseOnHover === 'true';
  }

  #bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.previous(true));
    this.nextBtn?.addEventListener('click', () => this.next(true));

    this.thumbnails.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.select(i, true));
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => {
        this.#isHovered = true;
        this.#stop();
      });
      this.addEventListener('mouseleave', () => {
        this.#isHovered = false;
        if (this.autoplay && this.#isVisible) this.play();
      });
    }

    this.addEventListener('focusin', () => this.#stop());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget) && this.autoplay && this.#isVisible && !this.#isHovered) {
        this.play();
      }
    });

    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.previous(true);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next(true);
      }
    });
  }

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
    } else if (this.autoplay && this.#isVisible && !this.#isHovered) {
      this.play();
    }
  };

  #setupVisibilityObserver() {
    this.#visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isVisible = entry.isIntersecting;
        if (this.#isVisible && this.autoplay && !this.#isHovered) {
          this.play();
        } else {
          this.#stop();
        }
      },
      { threshold: 0.1 }
    );
    this.#visibilityObserver.observe(this);
  }

  select(index, userInitiated = false) {
    const len = this.slides.length;
    if (len === 0) return;

    if (this.infinite) {
      index = ((index % len) + len) % len;
    } else {
      index = Math.max(0, Math.min(len - 1, index));
    }

    if (index === this.#current) return;

    const prevIndex = this.#current;

    this.slides[prevIndex]?.setAttribute('aria-hidden', 'true');
    this.slides[index]?.setAttribute('aria-hidden', 'false');

    this.thumbnails.forEach((thumb, i) => {
      thumb.setAttribute('aria-selected', String(i === index));
    });

    this.#current = index;

    this.dispatchEvent(
      new CustomEvent('hero-slider:change', {
        detail: { index, previousIndex: prevIndex, userInitiated },
        bubbles: true,
      })
    );

    if (userInitiated && this.autoplay && this.#isVisible && !this.#isHovered) {
      this.#stop();
      this.play();
    }
  }

  next(userInitiated = false) {
    this.select(this.#current + 1, userInitiated);
  }

  previous(userInitiated = false) {
    this.select(this.#current - 1, userInitiated);
  }

  play() {
    if (this.#interval) return;
    if (prefersReducedMotion()) return;
    if (!this.#isVisible) return;
    this.#interval = setInterval(() => this.next(false), this.autoplaySpeed);
  }

  pause() {
    this.#stop();
  }

  #stop() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
